import {
  Directive,
  Host,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { Key } from 'ts-key-enum';
import { Table } from 'primeng/table';

@Directive({
  selector: '[crmVisualSelectedColumnsTable]',
  standalone: true
})
export class VisualSelectedColumnsTableDirective implements OnInit, OnChanges, OnDestroy {
  @Input() public isVisualSelectedColumns = true;
  @Input() public keySelectedColumns: string;
  @Input() public loading: boolean;
  @Input() public paginationLoading: boolean;

  @Input() public groupByUnits: boolean;
  @Input() public groupByCostCenters: boolean;

  public visualSelectedColumns: string[] = [];
  private indexVisualSelectedColumns = 0;

  /** Leaf header row cells aligned with body `cellIndex` (incl. frozen column). */
  private mappingHeaderCells: HTMLTableCellElement[] = [];
  /** Selectable leaf headers (balance tables: `data-column-visible="true"`). */
  private headerCells: HTMLTableCellElement[] = [];
  private timer: ReturnType<typeof setTimeout>;
  private isDestroyed = false;

  public constructor(private readonly renderer: Renderer2, @Host() private readonly table: Table) {}

  public ngOnInit() {
    if (this.isVisualSelectedColumns) {
      this.scheduleColumnSync();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (!this.isVisualSelectedColumns) {
      return;
    }

    if (changes.loading?.currentValue === false) {
      this.scheduleColumnSync();
    }
    if (changes.paginationLoading?.currentValue === false) {
      this.scheduleColumnSync();
    }
    if (changes.groupByUnits && !changes.groupByUnits.firstChange) {
      this.scheduleColumnSync();
    }
    if (changes.groupByCostCenters && !changes.groupByCostCenters.firstChange) {
      this.scheduleColumnSync();
    }
  }

  public ngOnDestroy() {
    this.isDestroyed = true;
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  @HostListener('window:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    if (this.visualSelectedColumns?.length) {
      this.refreshHeaderCollections();

      if (this.headerCells?.length) {
        if (event.code === Key.ArrowLeft) {
          this.moveColumnSelection(-1, event.ctrlKey || event.metaKey);
          event.preventDefault();
        } else if (event.code === Key.ArrowRight) {
          this.moveColumnSelection(1, event.ctrlKey || event.metaKey);
          event.preventDefault();
        }
      }
    }
  }

  private moveColumnSelection(direction: number, isCtrlKey: boolean) {
    let currentIndex = 0;
    if (direction === 1) {
      const currentColumnName = this.visualSelectedColumns[this.visualSelectedColumns?.length - 1];
      currentIndex = this.headerCells?.findIndex((cell) => cell.getAttribute('id_column') === currentColumnName);
    } else {
      const currentColumnName = this.visualSelectedColumns[0];
      currentIndex = this.headerCells?.findIndex((cell) => cell.getAttribute('id_column') === currentColumnName);
    }

    if (currentIndex === -1) return;

    const newIndex = currentIndex + direction;
    if (newIndex < 0 || newIndex >= this.headerCells.length) return;

    const newColumnId = this.headerCells[newIndex].getAttribute('id_column');
    if (!newColumnId) return;

    if (!isCtrlKey) {
      this.clearAllColumnSelection();
    }

    this.selectColumn(direction, newColumnId);
    this.scrollColumnIntoView(newColumnId);
  }

  private scrollColumnIntoView(columnId: string) {
    const headerCell = this.headerCells?.find((el) => el.getAttribute('id_column') === columnId);
    if (headerCell) {
      const tableContainer = this.findTableContainer(headerCell);
      if (!tableContainer) return;
      const containerWidth = tableContainer.clientWidth;
      const cellLeft = headerCell.offsetLeft;
      const cellRight = cellLeft + headerCell.offsetWidth;
      const containerScrollLeft = tableContainer.scrollLeft;

      if (cellLeft < containerScrollLeft) {
        tableContainer.scrollTo({
          left: cellLeft,
          behavior: 'smooth'
        });
      } else if (cellRight > containerScrollLeft + containerWidth) {
        tableContainer.scrollTo({
          left: cellRight - containerWidth,
          behavior: 'smooth'
        });
      }
    }
  }

  private findTableContainer(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
      if (parent.scrollWidth > parent.clientWidth || parent.classList.contains('p-datatable-wrapper')) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private getTableElement(): HTMLTableElement | null {
    return (this.table?.tableViewChild?.nativeElement as HTMLTableElement) ?? null;
  }

  private getBodyRows(): HTMLTableRowElement[] {
    const tableEl = this.getTableElement();
    if (!tableEl) {
      return [];
    }

    return Array.from(tableEl.tBodies).reduce(
      (rows, body) => rows.concat(...Array.from(body.rows)),
      [] as HTMLTableRowElement[]
    );
  }

  private isBalanceReportTable(): boolean {
    return this.keySelectedColumns === 'balance-report' || this.keySelectedColumns === 'balance-favorite';
  }

  private getLeafHeaderCells(): HTMLTableCellElement[] {
    const tableEl = this.getTableElement();
    if (!tableEl) {
      return [];
    }

    const theadRows = tableEl.querySelectorAll('thead tr');
    if (!theadRows.length) {
      return [];
    }

    return Array.from(theadRows[theadRows.length - 1].querySelectorAll('th')) as HTMLTableCellElement[];
  }

  private refreshHeaderCollections(): void {
    const leafHeaders = this.getLeafHeaderCells();
    this.mappingHeaderCells = leafHeaders;

    if (this.isBalanceReportTable()) {
      this.headerCells = leafHeaders.filter((th) => th.getAttribute('data-column-visible') === 'true');
    } else {
      this.headerCells = leafHeaders;
    }
  }

  private resetColumnAnnotations(): void {
    this.indexVisualSelectedColumns = 0;
    this.visualSelectedColumns = [];

    const tableEl = this.getTableElement();
    if (!tableEl) {
      return;
    }

    tableEl.querySelectorAll('th[id_column], td[id_column]').forEach((cell) => {
      cell.removeAttribute('id_column');
      this.renderer.removeClass(cell, 'visualSelectedColumn');
    });
  }

  private getColumnCells(id_column: string): HTMLTableCellElement[] {
    const tableEl = this.getTableElement();
    if (!tableEl) {
      return [];
    }

    return Array.from(tableEl.querySelectorAll(`td[id_column="${id_column}"]`)) as HTMLTableCellElement[];
  }

  private mapBodyCellsToHeaders(): void {
    const rows = this.getBodyRows();
    if (!rows.length || !this.mappingHeaderCells.length) {
      return;
    }

    for (const row of rows) {
      const cells = Array.from(row.cells) as HTMLTableCellElement[];
      for (const cell of cells) {
        const headerCell = this.mappingHeaderCells[cell.cellIndex];
        const columnId = headerCell?.getAttribute('id_column');
        if (!columnId) {
          continue;
        }

        cell.setAttribute('id_column', columnId);
        if (headerCell.classList.contains('visualSelectedColumn')) {
          this.renderer.addClass(cell, 'visualSelectedColumn');
        } else {
          this.renderer.removeClass(cell, 'visualSelectedColumn');
        }
      }
    }
  }

  private handlerSelectedColumn() {
    if (this.isDestroyed) {
      return;
    }

    this.resetColumnAnnotations();
    this.refreshHeaderCollections();

    if (!this.headerCells?.length || !this.mappingHeaderCells.length) {
      return;
    }

    const clearAllColumnSelection = () => {
      if (!this.visualSelectedColumns?.length) {
        return;
      }

      const selectedHeaders = this.headerCells.filter((el) =>
        this.visualSelectedColumns.includes(el.getAttribute('id_column'))
      );

      for (const cell of selectedHeaders) {
        this.renderer.removeClass(cell, 'visualSelectedColumn');
      }

      for (const id of this.visualSelectedColumns) {
        this.getColumnCells(id).forEach((cell) => this.renderer.removeClass(cell, 'visualSelectedColumn'));
      }

      this.visualSelectedColumns = [];
    };

    const toggleColumn = (id_column: string, isCtrlKey: boolean) => {
      const headerCell = this.headerCells?.find((el) => el.getAttribute('id_column') === id_column);
      const columnCells = this.getColumnCells(id_column);

      if (this.visualSelectedColumns.includes(id_column)) {
        const isClickNotCtrlInMultiple = this.visualSelectedColumns?.length > 1 && !isCtrlKey;
        if (!isCtrlKey) {
          clearAllColumnSelection();
        }
        if (!isClickNotCtrlInMultiple) {
          this.visualSelectedColumns = this.visualSelectedColumns.filter((id) => id !== id_column);
          columnCells.forEach((cell) => this.renderer.removeClass(cell, 'visualSelectedColumn'));
          if (headerCell) this.renderer.removeClass(headerCell, 'visualSelectedColumn');
        } else {
          this.visualSelectedColumns.push(id_column);
          columnCells.forEach((cell) => this.renderer.addClass(cell, 'visualSelectedColumn'));
          if (headerCell) this.renderer.addClass(headerCell, 'visualSelectedColumn');
        }
      } else {
        if (!isCtrlKey) {
          clearAllColumnSelection();
        }
        this.visualSelectedColumns.push(id_column);
        columnCells.forEach((cell) => this.renderer.addClass(cell, 'visualSelectedColumn'));
        if (headerCell) this.renderer.addClass(headerCell, 'visualSelectedColumn');
      }
    };

    for (const headerCell of this.headerCells) {
      if (!headerCell.getAttribute('id_column') && !headerCell.getAttribute('data-column-selectable')) {
        const id_column = `${this.keySelectedColumns}_${this.indexVisualSelectedColumns++}`;
        headerCell.setAttribute('id_column', id_column);
        this.renderer.listen(headerCell, 'click', (event: MouseEvent) => {
          toggleColumn(id_column, event.ctrlKey || event.metaKey);
        });
      }
    }

    this.mapBodyCellsToHeaders();
  }

  /** Rebuild column annotations after table data/layout changes. */
  public scheduleColumnSync() {
    if (!this.isVisualSelectedColumns) {
      return;
    }

    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {
      this.handlerSelectedColumn();
    }, 300);
  }

  /** @deprecated use scheduleColumnSync */
  public updateColumns() {
    this.scheduleColumnSync();
  }

  public selectColumn(direction: number, id_column: string) {
    const headerCell = this.headerCells?.find((el) => el.getAttribute('id_column') === id_column);
    const columnCells = this.getColumnCells(id_column);

    if (!this.visualSelectedColumns.includes(id_column)) {
      direction === 1 ? this.visualSelectedColumns.push(id_column) : this.visualSelectedColumns.unshift(id_column);
    }

    columnCells.forEach((cell) => this.renderer.addClass(cell, 'visualSelectedColumn'));
    if (headerCell) {
      this.renderer.addClass(headerCell, 'visualSelectedColumn');
    }
  }

  public clearAllColumnSelection() {
    const selectedHeaders = this.headerCells?.filter((el) =>
      this.visualSelectedColumns.includes(el.getAttribute('id_column'))
    );

    if (selectedHeaders?.length) {
      for (const cell of selectedHeaders) {
        this.renderer.removeClass(cell, 'visualSelectedColumn');
      }
    }

    for (const id of this.visualSelectedColumns) {
      this.getColumnCells(id).forEach((cell) => this.renderer.removeClass(cell, 'visualSelectedColumn'));
    }

    this.visualSelectedColumns = [];
  }
}
